// fix todos about types in this file
import {
  ColumnDef,
  ColumnSort,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtual } from '@tanstack/react-virtual';
import cn from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { SharingInvitationStatus } from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import sadCatImg from '@/shared/assets/images/sad-cat.png';
import {
  NEW_LINE,
  PatientsListColumnName,
  SliceName,
  SortByFunctionType,
} from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Result, Skeleton } from '@/shared/ui';

import { DateOfBirthDropdownFilter } from '@/features/dateOfBirthDropdownFilter';
import { DoctorsDropdownFilter } from '@/features/doctorsDropdownFilter';
import { LastModifiedDropdownFilter } from '@/features/lastModifiedDropdownFilter';
import { StudiesDropdownFilter } from '@/features/studiesDropdownFilter';

import { RootState } from '@/app/model/store';

import styles from './TableWithInfiniteScroll.module.scss';
import { SortArrows } from './ui/SortArrows';

type TableWithInfiniteScrollProps<DataType> = {
  itemCounter: number;
  columnsScheme: ColumnDef<DataType>[];
  listData: DataType[];
  reFetchList: (id: string) => void;
  loadingState: keyof typeof SliceName;
  testID?: string;
  className?: string;
  showSharedByColumn?: boolean;
  showNothingFound?: boolean;
  showSkeleton?: boolean;
  showEmpty?: boolean;
  serverSideSorting?: boolean;
  sortBy?: SortByFunctionType;
  title?: string | React.ReactNode;
  emptyComponent?: React.ReactNode;
  clickOnRow?: (id: string) => void;
  getRowUrl?: (id: string) => string;
  conditionalAdditionalClickOnRow?: (id: string) => void;
  onDoctorFilterChange?: (doctorsIDs: string[]) => void;
  onStudyTypesFilterChange?: (
    studyTypes: string[],
    hasTranscriptor: boolean | undefined,
  ) => void;
  onAgeGroupFilterChange?: (ageGroup: string[]) => void;
  onLastModifiedFilterChange?: (dates: { from?: Date; to?: Date }) => void;
};

// TODO: [4/m]: there is no loading state in all slices
const getLoadingStateSelector =
  (loadingState: keyof typeof SliceName) => (state: RootState) =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    state[loadingState]?.loading;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const TableWithInfiniteScroll = <DataType extends Record<string, any>>(
  props: TableWithInfiniteScrollProps<DataType>,
) => {
  const {
    className,
    testID,
    showSharedByColumn = true,
    title,
    reFetchList,
    listData,
    itemCounter,
    clickOnRow,
    getRowUrl,
    conditionalAdditionalClickOnRow,
    columnsScheme,
    sortBy,
    showSkeleton,
    showNothingFound,
    showEmpty,
    emptyComponent,
    loadingState,
    serverSideSorting,
    onDoctorFilterChange,
    onStudyTypesFilterChange,
    onAgeGroupFilterChange,
    onLastModifiedFilterChange,
  } = props;

  // we need a reference to the scrolling element for logic down below
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const prevListDataLength = useRef(listData.length);

  const selectLoadingState = useMemo(
    () => getLoadingStateSelector(loadingState),
    [loadingState],
  );

  const listLoading = useAppSelector(selectLoadingState);

  const [sorting, setSorting] = useState<SortingState>([]);

  const totalDBRowCount = itemCounter ?? 0;

  const totalFetched = listData.length;

  const lastItemInListID = listData[listData.length - 1]?.ID;

  const { formatMessage } = useIntl();

  // called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        prevListDataLength.current = totalFetched;
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        const isNearBottom = scrollHeight - scrollTop - clientHeight < 300;
        const isAllFetched = totalDBRowCount - prevListDataLength.current <= 0;
        const isFetching = listLoading === 'pending' || listLoading === 'idle';
        const shouldFetchMore = isNearBottom && !isAllFetched && !isFetching;

        if (shouldFetchMore) {
          prevListDataLength.current = listData.length;
          reFetchList(lastItemInListID);
        }
      }
    },
    [
      totalFetched,
      totalDBRowCount,
      listData.length,
      lastItemInListID,
      listLoading,
      reFetchList,
    ],
  );

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, []);

  const handleSortChange = (sort: () => ColumnSort[]) => {
    if (sort().length) {
      const { id, desc } = sort()[0];

      if (sortBy) {
        sortBy(id as PatientsListColumnName, desc);
      }
    }

    setSorting(sort);
  };

  const table = useReactTable({
    data: listData,
    columns: columnsScheme,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: serverSideSorting,
    state: {
      sorting,
    },
    // Question: [1/l] What is the purpose of separate setSorting if setSorting calls inside handleSortChange?
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    onSortingChange: serverSideSorting ? handleSortChange : setSorting,
    enableSortingRemoval: false,
  });

  const { rows } = table.getRowModel();

  // Virtualizing is optional, but might be necessary if we are going to potentially have hundreds or thousands of rows
  const rowVirtualizer = useVirtual({
    parentRef: tableContainerRef,
    size: rows.length,
    overscan: 1200,
  });
  const { virtualItems: virtualRows } = rowVirtualizer;

  const paddingTop = virtualRows.length > 0 ? virtualRows?.[0]?.start || 0 : 0;

  return (
    <div
      className={cn(
        styles.container,
        showNothingFound && styles.displayGrid,
        className,
      )}
      data-testid={testID}
      onScroll={(event) => {
        fetchMoreOnBottomReached(event.target as HTMLDivElement);
      }}
      ref={tableContainerRef}
    >
      {title && <h2 className={cn(styles.title, 'h4')}>{title}</h2>}

      {showNothingFound && (
        <Result
          className={styles.empty}
          icon={
            <img
              src={sadCatImg}
              width={130}
              height={130}
              alt={formatMessage({
                id: 'imgAltText.sadCat',
                defaultMessage: 'Sad cat',
              })}
            />
          }
          text={formatMessage(
            {
              id: 'patientList.nothingFound',
              defaultMessage: `It looks like there are no patients with these search parameters.{newLine}Try to change search or filtering parameters`,
            },
            { newLine: NEW_LINE },
          )}
        />
      )}

      {!showNothingFound && (
        <table>
          <thead className={styles.tableHeader}>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isTabletColumnHidden =
                    header.id === PatientsListColumnName.SharedBy ||
                    header.id === PatientsListColumnName.SharingDate;

                  const isSortActive =
                    header.column.columnDef.enableSorting ?? true;

                  const isHideColumn =
                    header.column.columnDef.enableHiding ?? false;

                  const isSharedByColumnHidden =
                    !showSharedByColumn && isHideColumn;

                  const isPatientNameColumn =
                    header.id === PatientsListColumnName.PatientName;
                  const isDoctorsColumn =
                    header.id === PatientsListColumnName.Doctors;
                  const isDateOfBirthColumn =
                    header.id === PatientsListColumnName.DateOfBirth;
                  const isLastModifiedColumn =
                    header.id === PatientsListColumnName.LastModified;
                  const isStudiesColumn =
                    header.id === PatientsListColumnName.Studies;

                  return (
                    <th
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn(
                        styles.tableHeaderColumn,
                        isTabletColumnHidden && styles.displayNoneForTablet,
                        isSharedByColumnHidden && styles.displayNone,
                        'p2',
                      )}
                      style={{ width: header.getSize() }}
                    >
                      <div
                        className={cn(
                          styles.tableHeaderColumnWrapper,
                          !header.column.getCanSort() && styles.cursorDefault,
                        )}
                        onClick={(event) => {
                          if (
                            isDoctorsColumn ||
                            isDateOfBirthColumn ||
                            isStudiesColumn
                          ) {
                            return;
                          }

                          if (isPatientNameColumn) {
                            const currentSortDirection =
                              header.column.getIsSorted();

                            trackEvent({
                              eventName:
                                'PatientsPagePatientNameSortingClicked',
                              properties: {
                                sorting_state: currentSortDirection
                                  ? currentSortDirection
                                  : 'asc',
                              },
                            });
                          }

                          const handler =
                            header.column.getToggleSortingHandler();
                          if (handler) {
                            handler(event);
                          }
                        }}
                      >
                        {isDoctorsColumn &&
                        typeof onDoctorFilterChange === 'function' ? (
                          <DoctorsDropdownFilter
                            onChange={onDoctorFilterChange}
                          />
                        ) : isDateOfBirthColumn ? (
                          <DateOfBirthDropdownFilter
                            onChange={onAgeGroupFilterChange}
                          />
                        ) : isLastModifiedColumn &&
                          typeof onLastModifiedFilterChange === 'function' ? (
                          <>
                            <LastModifiedDropdownFilter
                              onChange={onLastModifiedFilterChange}
                            />
                            {isSortActive && (
                              <SortArrows
                                sortDirection={header.column.getIsSorted()}
                              />
                            )}
                          </>
                        ) : isStudiesColumn ? (
                          <StudiesDropdownFilter
                            onChange={onStudyTypesFilterChange}
                          />
                        ) : (
                          <>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}

                            {isSortActive && (
                              <SortArrows
                                sortDirection={header.column.getIsSorted()}
                              />
                            )}
                          </>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          {!showEmpty && (
            <tbody>
              {showSkeleton && (
                <tr>
                  <td colSpan={columnsScheme.length}>
                    <div className={styles.skeletonContainer}>
                      <Skeleton.Row rowsQuantity={4} />
                    </div>
                  </td>
                </tr>
              )}

              {paddingTop > 0 && (
                <tr>
                  <td style={{ height: `${paddingTop}px` }} />
                </tr>
              )}

              {virtualRows.map((virtualRow) => {
                const row = rows[virtualRow.index] as Row<DataType>;

                return (
                  <tr
                    key={row.id}
                    className={cn(
                      styles.tableRow,
                      !(row.original?.Paid ?? true) &&
                        !(row.original?.Overdue ?? true) &&
                        styles.newInvoice,
                      row.original?.Overdue && styles.overdueInvoice,
                      row.original?.isDeleted && styles.deletedRow,
                      typeof clickOnRow === 'function' && styles.cursorPointer,
                      'p2',
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const isDeleteColumn =
                        cell.column.id === PatientsListColumnName.Delete;

                      const isHideColumn = cell.column.columnDef.enableHiding;

                      const isTabletColumnHidden =
                        cell.column.id === PatientsListColumnName.SharedBy ||
                        cell.column.id === PatientsListColumnName.SharingDate;

                      const isSharedByColumnHidden =
                        !showSharedByColumn && isHideColumn;

                      const isTokenAndStatusPending =
                        row.original.Token &&
                        row.original.Status ===
                          SharingInvitationStatus.SharingInvitationStatus_Pending;

                      const getRowId = () => {
                        if (isTokenAndStatusPending) {
                          return row.original.Token;
                        }

                        return row.original.PatientID || row.original.ID;
                      };

                      return (
                        <td
                          key={cell.id}
                          className={cn(
                            styles.tableCell,
                            isDeleteColumn && styles.deleteColum,
                            isTabletColumnHidden && styles.displayNoneForTablet,
                            isSharedByColumnHidden && styles.displayNone,
                          )}
                          onClick={() => {
                            if (isDeleteColumn) {
                              return;
                            }

                            if (
                              isTokenAndStatusPending &&
                              conditionalAdditionalClickOnRow
                            ) {
                              conditionalAdditionalClickOnRow(
                                row.original.Token,
                              );
                            } else {
                              typeof clickOnRow === 'function' &&
                                clickOnRow(getRowId());
                            }
                          }}
                          onAuxClick={(event) => {
                            if (
                              event.button === 1 &&
                              getRowUrl &&
                              !isDeleteColumn
                            ) {
                              event.preventDefault();
                              window.open(getRowUrl(getRowId()), '_blank');
                            }
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,

                            cell.getContext(),
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          )}
        </table>
      )}

      {showEmpty && emptyComponent}
    </div>
  );
};
